/**
 * PublicGpPreferences — Iter 83.
 * Public page (no auth) reached at /gp/preferences/{code}. Lets the
 * referring GP opt in/out of each message type we'd send them. Re-uses
 * the same tokenised referral code so we don't need a separate GP login.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Bell, Save, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import AppFooter from '../components/AppFooter';
import { t } from '../lib/i18n';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '') + '/api';

export default function PublicGpPreferences() {
  const locale = (typeof window !== 'undefined' && localStorage.getItem('locale')) || 'en';
  const { code } = useParams();
  const [meta, setMeta] = useState(null);
  const [prefs, setPrefs] = useState({
    outcomes_report: true,
    status_replies: true,
    thank_you_nudge: true,
  });
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/gp/preferences/${code}`)
      .then((r) => {
        setMeta(r.data);
        const p = r.data.prefs || {};
        setPrefs({
          outcomes_report: p.outcomes_report ?? true,
          status_replies:  p.status_replies  ?? true,
          thank_you_nudge: p.thank_you_nudge ?? true,
        });
        setNote(p.note || '');
      })
      .catch(() => setErr(t(locale, 'referral_link_invalid')))
      .finally(() => setLoading(false));
  }, [code, locale]);

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true); setErr('');
    try {
      await axios.put(`${API}/gp/preferences/${code}`, { ...prefs, note });
      setDone(true);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="p-8 text-center text-fg-muted">Loading…</div>;
  }
  if (err && !meta) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8 text-center" data-testid="gp-prefs-invalid">
        <AlertCircle className="w-10 h-10 text-[#B83A3A] mb-3"/>
        <h2 className="font-heading text-xl font-bold mb-1">{t(locale, 'referral_link_invalid')}</h2>
        <p className="text-sm text-fg-muted">{err}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-bg-secondary/30" data-testid="gp-prefs-page">
      <header className="bg-bg-primary border-b border-soft py-4 px-6">
        <BrandLogo size={32}/>
      </header>
      <main className="flex-1 flex items-start justify-center p-4 md:p-8">
        <div className="w-full max-w-xl">
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">
            <Bell className="inline w-3.5 h-3.5 -mt-0.5 mr-1"/> {meta?.clinic_name || 'Physiogenic'}
          </div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight" data-testid="gp-prefs-title">
            {t(locale, 'gp_prefs_title')}
          </h1>
          <p className="text-fg-muted text-sm mt-1 mb-6">{t(locale, 'gp_prefs_lede')}</p>

          {done ? (
            <div className="bento-card text-center p-8" data-testid="gp-prefs-success">
              <CheckCircle2 className="w-12 h-12 text-[#1B5E20] mx-auto mb-3"/>
              <p className="text-sm">{t(locale, 'preferences_saved')}</p>
            </div>
          ) : (
            <form onSubmit={save} className="bento-card space-y-4" data-testid="gp-prefs-form">
              <Toggle
                checked={prefs.outcomes_report}
                onChange={(v) => setPrefs({ ...prefs, outcomes_report: v })}
                label={t(locale, 'pref_outcomes_report')}
                hint={t(locale, 'pref_outcomes_help')}
                testid="gp-prefs-outcomes"
              />
              <Toggle
                checked={prefs.status_replies}
                onChange={(v) => setPrefs({ ...prefs, status_replies: v })}
                label={t(locale, 'pref_status_replies')}
                hint={t(locale, 'pref_status_help')}
                testid="gp-prefs-status"
              />
              <Toggle
                checked={prefs.thank_you_nudge}
                onChange={(v) => setPrefs({ ...prefs, thank_you_nudge: v })}
                label={t(locale, 'pref_thank_you')}
                hint={t(locale, 'pref_thank_you_help')}
                testid="gp-prefs-thanks"
              />
              <label className="block">
                <div className="text-xs text-fg-muted mb-1">{t(locale, 'note_optional')}</div>
                <textarea className="input min-h-[60px]" value={note}
                          onChange={(e) => setNote(e.target.value)}
                          data-testid="gp-prefs-note"/>
              </label>
              {err && (
                <div className="text-sm text-[#B83A3A]" data-testid="gp-prefs-err">{err}</div>
              )}
              <button type="submit" disabled={saving}
                      className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                      data-testid="gp-prefs-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
                {t(locale, 'save_preferences')}
              </button>
            </form>
          )}
        </div>
      </main>
      <AppFooter/>
    </div>
  );
}

function Toggle({ checked, onChange, label, hint, testid }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input type="checkbox" checked={checked}
             onChange={(e) => onChange(e.target.checked)}
             className="mt-1"
             data-testid={testid}/>
      <div className="flex-1">
        <div className="text-sm font-medium">{label}</div>
        {hint && <div className="text-[11px] text-fg-muted mt-0.5">{hint}</div>}
      </div>
    </label>
  );
}
